import { Button, ButtonIcon } from '@/components/ui/button'
import { HStack } from '@/components/ui/hstack'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import { ArtifactPart, ConversationMessage, selectConversation } from '@/redux/slices/aiChatSlice'
import { useAppSelector } from '@/redux/store'
import { ArtifactTypeEnum } from '@/typings/domain'
import { FlashList, FlashListRef } from '@shopify/flash-list'
import { ChevronDownIcon } from 'lucide-react-native'
import { memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NativeScrollEvent, NativeSyntheticEvent, View } from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { Markdown } from 'react-native-remark'
import AIAvatar from '../aiConnection/AIAvatar'
import AIGeneratedFormArtifactPart, { AIGeneratedFormDescriptionRendererWrapper } from './AIGeneratedFormArtifactPart'
import AIMessageStatus from './AIMessageStatus'
import AttachmentItem from './AttachmentItem'

type ConversationProps = {
  conversationId: string
  paddingTop: number
}

export default function Conversation({ conversationId, paddingTop }: ConversationProps) {
  const listRef = useRef<FlashListRef<ConversationMessage>>(null)

  const messages = useAppSelector(state => selectConversation(state.aiChat, conversationId)?.messages)

  const isUserTouchingRef = useRef(false)
  const autoScrollDisabledRef = useRef(false)
  const [scrollToBottomVisible, setScrollToBottomVisible] = useState(false)

  const handleTouchStart = useCallback(() => {
    isUserTouchingRef.current = true
    autoScrollDisabledRef.current = true
  }, [])

  const handleTouchEnd = useCallback(() => {
    isUserTouchingRef.current = false
  }, [])

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollY = event.nativeEvent.contentOffset.y
    const contentHeight = event.nativeEvent.contentSize.height
    const layoutHeight = event.nativeEvent.layoutMeasurement.height

    const isNotReachedBottom = contentHeight > layoutHeight && scrollY + layoutHeight + 100 < contentHeight
    setScrollToBottomVisible(autoScrollDisabledRef.current && isNotReachedBottom)

    if (scrollY + layoutHeight + 10 >= contentHeight) autoScrollDisabledRef.current = false
  }, [])

  const handleContentSizeChange = useCallback(() => {
    if (!autoScrollDisabledRef.current && !isUserTouchingRef.current) {
      listRef.current?.scrollToEnd({ animated: true })
    }
  }, [])

  const handleScrollToBottom = useCallback(() => {
    autoScrollDisabledRef.current = false
    listRef.current?.scrollToEnd({ animated: true })
  }, [])

  return (
    <VStack className="flex-1 relative">
      <AIGeneratedFormDescriptionRendererWrapper>
        <FlashList
          ref={listRef}
          className="flex-1"
          contentContainerStyle={{ paddingTop }}
          contentContainerClassName="pb-2"
          keyExtractor={item => item.id}
          data={messages ?? []}
          renderItem={({ item }) => <Message conversationId={conversationId} message={item} />}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onContentSizeChange={handleContentSizeChange}
          onScroll={handleScroll}
        />
      </AIGeneratedFormDescriptionRendererWrapper>

      {scrollToBottomVisible && (
        <Animated.View entering={FadeIn.duration(300)} exiting={FadeOut.duration(300)}>
          <Button
            className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full w-10 h-10 bg-background-0 data-[active=true]:bg-background-100 border border-typography-50 shadow-[0px_0px_10px_-5px_#0000000a,0px_10px_25px_-5px_#0000001a]"
            action="secondary"
            onPress={handleScrollToBottom}
          >
            <ButtonIcon as={ChevronDownIcon} size={20} />
          </Button>
        </Animated.View>
      )}
    </VStack>
  )
}

type MessageProps = {
  conversationId: string
  message: ConversationMessage
}

const Message = memo(function Message(props: MessageProps) {
  return props.message.role === 'user' ? <UserMessage {...props} /> : <AIMessage {...props} />
})

function UserMessage({ message }: MessageProps) {
  if (!message.parts.length) return null

  const attachmentItems = message.parts.filter(part => part.type === 'attachment')
  const textItems = message.parts.filter(part => part.type === 'text')

  return (
    <VStack className="max-w-[70%] items-end ml-auto my-4 px-4">
      <HStack space="sm">
        {attachmentItems.map(item => (
          <AttachmentItem key={`${item.type}-${item.key ?? ''}`} attachment={item.attachment} size="lg" showFileName />
        ))}
      </HStack>
      {textItems.map(item => (
        <View key={`${item.type}-${item.key ?? ''}`} className="bg-background-100 px-4 py-2 rounded-lg">
          <Text className="text-lg text-typography-900">{item.content}</Text>
        </View>
      ))}
    </VStack>
  )
}

function AIMessage({ conversationId, message }: MessageProps) {
  const { t } = useTranslation('aiChat')

  return (
    <View className="p-4 my-2 overflow-hidden">
      <AIAvatar active={!message.done} className="w-8 mb-2" />

      {message.isCheckRiskRejectedMessage ? (
        <Text>{t('checkRiskRejected')}</Text>
      ) : (
        message.parts.map(part =>
          part.type === 'text' ? (
            <Markdown key={`${part.type}-${part.key ?? ''}`} markdown={part.content} />
          ) : part.type === 'error' ? (
            <Text key={`${part.type}-${part.key ?? ''}`} className="text-red-500">
              {part.message}
            </Text>
          ) : part.type === 'artifact' ? (
            <AIMessageArtifactPart key={`${part.type}-${part.key ?? ''}`} conversationId={conversationId} part={part} />
          ) : null
        )
      )}

      <AIMessageStatus message={message} />

      {message.interrupted && !message.isCheckRiskRejectedMessage && (
        <Text className="text-lg text-typography-400">已停止生成</Text>
      )}
    </View>
  )
}

type AIMessageArtifactPartProps = {
  conversationId: string
  part: ArtifactPart
}

function AIMessageArtifactPart({ conversationId, part }: AIMessageArtifactPartProps) {
  if (part.artifactType === ArtifactTypeEnum.Form)
    return <AIGeneratedFormArtifactPart conversationId={conversationId} part={part} />

  return null
}
